import React, { useCallback, useMemo, useState } from 'react';

// DEPENDENCIES
import HTMLReactParser from 'html-react-parser';
import { useSelector } from 'react-redux';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';

// GLOBAL FUNCTIONS
import { apiFetch, formatDate } from 'functions.js';

// HELPERS
import { useDealTasks } from '../helpers/DealTasksProvider';

// BOOTSTRAP COMPONENTS
import { Row, Col } from 'react-bootstrap';

// CORE
import Form from 'core/form/Form.js';

// STYLES
import styles from './taskItem.module.scss';
import TaskComments from './TaskComments';

// MAIN COMPONENT
const TaskItem = ({ taskNumber, task, rowNumber }) => {
  const { task_id } = task || {};

  // REDUX STATE
  const { first_name, last_name } = useSelector(state => state.user) || {};

  // HOOKS
  const { tasksCount, setTaskCount, tasksCompleted, toggleTaskCompleted } = useDealTasks() || {};

  // STATE
  const [completedDate, setCompletedDate] = useState();

  // MEMOS
  const count = useMemo(() => tasksCount[rowNumber - 1], [tasksCount, rowNumber]);
  const completed = useMemo(() => tasksCompleted[task_id], [task_id, tasksCompleted]);

  // CALLBACKS
  const handleChange = useCallback(
    e => {
      const isCompleted = e.target.checked;
      toggleTaskCompleted(task_id);
      apiFetch({
        method: 'PUT',
        endpoint: ENDPOINTS.dealTasks.complete,
        params: {
          task_id,
          is_completed: isCompleted ? 1 : 0
        },
        loadingMessage: `${isCompleted ? 'Completing' : 'Opening'} task`,
        successMessage: `Successfully ${isCompleted ? 'completed' : 'opened'} task.`,
        errorMessage: `Unable to ${isCompleted ? 'complete' : 'open'} comment.`,
        onError: () => toggleTaskCompleted(task_id),
        onSuccess: () => {
          setCompletedDate(isCompleted ? new Date() : undefined);
          const index = rowNumber - 1;
          setTaskCount(index, isCompleted ? count - 1 : count + 1);
        }
      });
    },
    [toggleTaskCompleted, task_id, rowNumber, setTaskCount, count]
  );

  // RENDER
  return (
    <div className={styles.task}>
      <div className={styles.taskData}>
        <div className={styles.rowHeader}>
          <Form.Checkbox className={styles.checkbox} checked={completed} onChange={handleChange} />
          <span className={styles.taskNumber}>{taskNumber}</span>
        </div>
        <div>
          <Row className={styles.row}>
            <Col xs={24} lg={6} className="text-break">
              {HTMLReactParser(task.task_description)}
            </Col>
            <Col xs={24} lg={9} className={styles.comments}>
              <TaskComments task={task} rowNumber={rowNumber} />
            </Col>
            <Col xs={24} lg={6}>
              <div className={styles.metadata}>
                <div>
                  <span className="text-muted">Created on</span>&nbsp;
                  {formatDate(task.task_created_date)}&nbsp;
                  <span className="text-muted">by</span>&nbsp;
                  {task.task_created_by}
                </div>
                {completedDate && (
                  <div>
                    <span className="text-muted">Completed on</span>&nbsp;
                    {formatDate(new Date())}&nbsp;
                    <span className="text-muted">by</span>&nbsp;
                    {first_name}&nbsp;{last_name}
                  </div>
                )}
              </div>
            </Col>
            <Col md={24} lg={3}>
              Age: {task.task_age}
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

// EXPORT
export default TaskItem;
